/**
 * CursorAnimations - Animation helpers for natural cursor movement
 *
 * Handles:
 * - Bezier curve generation for smooth paths
 * - Human-like movement patterns
 * - Path interpolation
 * - Easing functions
 * - Movement variations
 */
export default class CursorAnimations {
  constructor(cursor) {
    this.cursor = cursor
  }

  /**
   * Generate movement path between two points
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @param {string} pathType - Type of path (linear, bezier, arc, human)
   * @param {Object} options - Path generation options
   * @returns {Array} - Array of position objects
   */
  generateMovementPath(start, end, pathType = 'human', options = {}) {
    const { segments = 50, variation = 0.1, controlPointDistance = 0.3 } = options

    switch (pathType) {
      case 'linear':
        return this.generateLinearPath(start, end, segments)

      case 'bezier':
        return this.generateBezierPath(start, end, segments, controlPointDistance)

      case 'arc':
        return this.generateArcPath(start, end, segments, options)

      case 'human':
      default:
        return this.generateHumanPath(start, end, segments, variation)
    }
  }

  /**
   * Generate linear path
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @param {number} segments - Number of path segments
   * @returns {Array} - Array of position objects
   */
  generateLinearPath(start, end, segments) {
    const path = []

    for (let i = 0; i <= segments; i++) {
      const t = i / segments
      const x = start.x + (end.x - start.x) * t
      const y = start.y + (end.y - start.y) * t
      path.push({ x, y })
    }

    return path
  }

  /**
   * Generate bezier curve path
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @param {number} segments - Number of path segments
   * @param {number} controlPointDistance - Distance of control points (0-1)
   * @returns {Array} - Array of position objects
   */
  generateBezierPath(start, end, segments, controlPointDistance = 0.3) {
    const path = []

    // Calculate control points
    const distance = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))

    const controlDistance = distance * controlPointDistance

    // Generate control points with some randomness
    const angle = Math.atan2(end.y - start.y, end.x - start.x)
    const perpendicular = angle + Math.PI / 2

    const cp1x =
      start.x +
      Math.cos(angle) * controlDistance * 0.5 +
      Math.cos(perpendicular) * controlDistance * (Math.random() - 0.5)
    const cp1y =
      start.y +
      Math.sin(angle) * controlDistance * 0.5 +
      Math.sin(perpendicular) * controlDistance * (Math.random() - 0.5)

    const cp2x =
      end.x -
      Math.cos(angle) * controlDistance * 0.5 +
      Math.cos(perpendicular) * controlDistance * (Math.random() - 0.5)
    const cp2y =
      end.y -
      Math.sin(angle) * controlDistance * 0.5 +
      Math.sin(perpendicular) * controlDistance * (Math.random() - 0.5)

    // Generate bezier curve points
    for (let i = 0; i <= segments; i++) {
      const t = i / segments
      const pos = this.cubicBezier(start, { x: cp1x, y: cp1y }, { x: cp2x, y: cp2y }, end, t)
      path.push(pos)
    }

    return path
  }

  /**
   * Generate arc path
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @param {number} segments - Number of path segments
   * @param {Object} options - Arc options
   * @returns {Array} - Array of position objects
   */
  generateArcPath(start, end, segments, options = {}) {
    const { arcHeight = 50, clockwise = true } = options

    const path = []
    const midX = (start.x + end.x) / 2
    const midY = (start.y + end.y) / 2

    // Calculate perpendicular direction for arc
    const dx = end.x - start.x
    const dy = end.y - start.y
    const perpLength = Math.sqrt(dx * dx + dy * dy)

    if (perpLength === 0) {
      return [start, end]
    }

    const perpX = -dy / perpLength
    const perpY = dx / perpLength

    const arcCenterX = midX + perpX * arcHeight * (clockwise ? 1 : -1)
    const arcCenterY = midY + perpY * arcHeight * (clockwise ? 1 : -1)

    for (let i = 0; i <= segments; i++) {
      const t = i / segments
      const x = start.x + (end.x - start.x) * t + perpX * arcHeight * (clockwise ? 1 : -1) * Math.sin(t * Math.PI)
      const y = start.y + (end.y - start.y) * t + perpY * arcHeight * (clockwise ? 1 : -1) * Math.sin(t * Math.PI)
      path.push({ x, y })
    }

    return path
  }

  /**
   * Generate human-like movement path
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @param {number} segments - Number of path segments
   * @param {number} variation - Movement variation (0-1)
   * @returns {Array} - Array of position objects
   */
  generateHumanPath(start, end, segments, variation = 0.1) {
    const path = []
    const distance = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))

    // Add some initial hesitation
    const hesitationPoints = Math.floor(segments * 0.1)

    for (let i = 0; i <= segments; i++) {
      let t = i / segments

      // Apply easing - humans don't move linearly
      t = this.humanEasing(t)

      let x = start.x + (end.x - start.x) * t
      let y = start.y + (end.y - start.y) * t

      // Add natural variation
      if (i > 0 && i < segments) {
        const variationAmount = variation * distance * 0.01
        const noise = this.perlinNoise(i * 0.1, 0) * variationAmount
        const perpendicular = Math.atan2(end.y - start.y, end.x - start.x) + Math.PI / 2

        x += Math.cos(perpendicular) * noise
        y += Math.sin(perpendicular) * noise
      }

      // Add micro-corrections (small adjustments)
      if (i > segments * 0.3 && i < segments * 0.9) {
        const correction = (Math.random() - 0.5) * 2
        x += correction
        y += correction
      }

      path.push({ x, y })
    }

    return path
  }

  /**
   * Generate idle animation path around a central point
   * @param {Object} center - Center position {x, y}
   * @param {Object} options - Idle options
   * @returns {Array} - Array of position objects for idle animation
   */
  generateIdlePath(center, options = {}) {
    const {
      radius = 5, // Max distance from center
      points = 30, // Number of points in the path
      loops = 2, // Number of loops in the path
      variation = 0.3, // Randomness factor (0-1)
    } = options

    const path = []

    // Generate a natural-looking idle path
    for (let i = 0; i <= points; i++) {
      const t = i / points

      // Base circular motion with some loops
      const angle = t * Math.PI * 2 * loops

      // Vary the radius slightly for each point
      const currentRadius = radius * (0.5 + Math.sin(t * Math.PI * 4) * 0.5)

      // Add some randomness to make it look more natural
      const randomX = (Math.random() - 0.5) * radius * variation
      const randomY = (Math.random() - 0.5) * radius * variation

      // Calculate position with slight randomness
      const x = center.x + Math.cos(angle) * currentRadius + randomX
      const y = center.y + Math.sin(angle) * currentRadius + randomY

      path.push({ x, y })
    }

    // Smooth the path to remove any sharp movements
    return this.smoothPath(path, 3)
  }

  /**
   * Calculate cubic bezier curve point
   * @param {Object} p0 - Start point
   * @param {Object} p1 - First control point
   * @param {Object} p2 - Second control point
   * @param {Object} p3 - End point
   * @param {number} t - Parameter (0-1)
   * @returns {Object} - Position {x, y}
   */
  cubicBezier(p0, p1, p2, p3, t) {
    const oneMinusT = 1 - t
    const oneMinusT2 = oneMinusT * oneMinusT
    const oneMinusT3 = oneMinusT2 * oneMinusT
    const t2 = t * t
    const t3 = t2 * t

    return {
      x: oneMinusT3 * p0.x + 3 * oneMinusT2 * t * p1.x + 3 * oneMinusT * t2 * p2.x + t3 * p3.x,
      y: oneMinusT3 * p0.y + 3 * oneMinusT2 * t * p1.y + 3 * oneMinusT * t2 * p2.y + t3 * p3.y,
    }
  }

  /**
   * Human-like easing function
   * @param {number} t - Parameter (0-1)
   * @returns {number} - Eased value
   */
  humanEasing(t) {
    // Combination of ease-in-out with some imperfection
    const easeInOut = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    // Add slight imperfection
    const imperfection = Math.sin(t * Math.PI * 3) * 0.02

    return Math.max(0, Math.min(1, easeInOut + imperfection))
  }

  /**
   * Simple Perlin noise implementation
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {number} - Noise value (-1 to 1)
   */
  perlinNoise(x, y) {
    // Simplified noise for natural movement variation
    const xi = Math.floor(x)
    const yi = Math.floor(y)
    const xf = x - xi
    const yf = y - yi

    const u = this.fade(xf)
    const v = this.fade(yf)

    const n00 = this.grad(this.hash(xi, yi), xf, yf)
    const n01 = this.grad(this.hash(xi, yi + 1), xf, yf - 1)
    const n10 = this.grad(this.hash(xi + 1, yi), xf - 1, yf)
    const n11 = this.grad(this.hash(xi + 1, yi + 1), xf - 1, yf - 1)

    return this.lerp(v, this.lerp(u, n00, n10), this.lerp(u, n01, n11))
  }

  /**
   * Fade function for noise
   * @param {number} t - Parameter
   * @returns {number} - Faded value
   */
  fade(t) {
    return t * t * t * (t * (t * 6 - 15) + 10)
  }

  /**
   * Hash function for noise
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {number} - Hash value
   */
  hash(x, y) {
    return ((x * 374761393 + y * 668265263) % 1073741824) % 256
  }

  /**
   * Gradient function for noise
   * @param {number} hash - Hash value
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {number} - Gradient value
   */
  grad(hash, x, y) {
    const h = hash & 3
    const u = h < 2 ? x : y
    const v = h < 2 ? y : x
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v)
  }

  /**
   * Linear interpolation
   * @param {number} t - Parameter (0-1)
   * @param {number} a - Start value
   * @param {number} b - End value
   * @returns {number} - Interpolated value
   */
  lerp(t, a, b) {
    return a + t * (b - a)
  }

  /**
   * Get position from path at specific progress
   * @param {Array} path - Array of position objects
   * @param {number} progress - Progress (0-1)
   * @returns {Object} - Position {x, y}
   */
  getPositionFromPath(path, progress) {
    // Safety checks for undefined or invalid paths
    if (!path || !Array.isArray(path)) return { x: 0, y: 0 }
    if (path.length === 0) return { x: 0, y: 0 }
    if (path.length === 1) return { ...path[0] }

    const maxIndex = path.length - 1
    const exactIndex = progress * maxIndex
    const lowerIndex = Math.floor(exactIndex)
    const upperIndex = Math.min(Math.ceil(exactIndex), maxIndex)

    // Additional safety checks for indices
    if (lowerIndex < 0 || lowerIndex >= path.length || upperIndex < 0 || upperIndex >= path.length) {
      return { ...path[0] } // Return first point if indices are out of bounds
    }

    if (lowerIndex === upperIndex) {
      return { ...path[lowerIndex] }
    }

    const t = exactIndex - lowerIndex
    const start = path[lowerIndex]
    const end = path[upperIndex]

    // Safety check for start and end points
    if (
      !start ||
      !end ||
      typeof start.x !== 'number' ||
      typeof start.y !== 'number' ||
      typeof end.x !== 'number' ||
      typeof end.y !== 'number'
    ) {
      return { x: 0, y: 0 }
    }

    return {
      x: start.x + (end.x - start.x) * t,
      y: start.y + (end.y - start.y) * t,
    }
  }

  /**
   * Smooth path using moving average
   * @param {Array} path - Array of position objects
   * @param {number} windowSize - Smoothing window size
   * @returns {Array} - Smoothed path
   */
  smoothPath(path, windowSize = 3) {
    if (path.length <= windowSize) return [...path]

    const smoothed = []
    const halfWindow = Math.floor(windowSize / 2)

    for (let i = 0; i < path.length; i++) {
      const start = Math.max(0, i - halfWindow)
      const end = Math.min(path.length - 1, i + halfWindow)

      let totalX = 0
      let totalY = 0
      let count = 0

      for (let j = start; j <= end; j++) {
        totalX += path[j].x
        totalY += path[j].y
        count++
      }

      smoothed.push({
        x: totalX / count,
        y: totalY / count,
      })
    }

    return smoothed
  }

  /**
   * Add tremor effect to path (simulate hand shake)
   * @param {Array} path - Array of position objects
   * @param {number} intensity - Tremor intensity (0-1)
   * @returns {Array} - Path with tremor effect
   */
  addTremor(path, intensity = 0.1) {
    return path.map((point, index) => {
      if (index === 0 || index === path.length - 1) {
        return point // Don't add tremor to start/end points
      }

      const tremor = intensity * 5
      const x = point.x + (Math.random() - 0.5) * tremor
      const y = point.y + (Math.random() - 0.5) * tremor

      return { x, y }
    })
  }

  /**
   * Create overshoot effect (cursor goes slightly past target then corrects)
   * @param {Array} path - Array of position objects
   * @param {number} overshootAmount - Overshoot distance
   * @returns {Array} - Path with overshoot effect
   */
  addOvershoot(path, overshootAmount = 10) {
    if (path.length < 2) return path

    const lastPoint = path[path.length - 1]
    const secondLastPoint = path[path.length - 2]

    // Calculate overshoot direction
    const dx = lastPoint.x - secondLastPoint.x
    const dy = lastPoint.y - secondLastPoint.y
    const length = Math.sqrt(dx * dx + dy * dy)

    if (length === 0) return path

    const normalizedDx = dx / length
    const normalizedDy = dy / length

    // Create overshoot point
    const overshootPoint = {
      x: lastPoint.x + normalizedDx * overshootAmount,
      y: lastPoint.y + normalizedDy * overshootAmount,
    }

    // Create correction path
    const correctionPath = []
    const correctionSegments = 5

    for (let i = 0; i <= correctionSegments; i++) {
      const t = i / correctionSegments
      const easedT = this.easeOut(t)

      correctionPath.push({
        x: overshootPoint.x + (lastPoint.x - overshootPoint.x) * easedT,
        y: overshootPoint.y + (lastPoint.y - overshootPoint.y) * easedT,
      })
    }

    return [...path, overshootPoint, ...correctionPath]
  }

  /**
   * Ease out function
   * @param {number} t - Parameter (0-1)
   * @returns {number} - Eased value
   */
  easeOut(t) {
    return 1 - Math.pow(1 - t, 3)
  }

  /**
   * Ease in function
   * @param {number} t - Parameter (0-1)
   * @returns {number} - Eased value
   */
  easeIn(t) {
    return t * t * t
  }

  /**
   * Ease in-out function
   * @param {number} t - Parameter (0-1)
   * @returns {number} - Eased value
   */
  easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }
}
